import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { MatchState } from 'api/responseTypes';
import { EventHeader } from 'components/EventHeader/EventHeader';
import { GoodToKnowCard } from 'components/GoodToKnow/GoodToKnowCard';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import { useEventTracking } from 'hooks/useEventTracking';
import { useInternationalEventDetailQuery } from 'pages/InternationalEvents/hooks/useInternationalEventDetail';
import { useIsInternationalEventsEnabled } from 'pages/InternationalEvents/hooks/useInternationalEvents';
import { ROUTES } from 'router/routes';

/**
 * Display-only detail page for an international event. Mirrors the concert page
 * (shared EventHeader hero) but has NO Seatsio hall, NO TicketCart, and NO
 * cart/checkout — booking happens on PlatinumList via the "احجز الآن" CTA.
 */
export const InternationalEventDetailPage = () => {
  const navigate = useNavigate();
  const { eventKey } = useParams();
  const { eventTracking } = useEventTracking();
  const isEnabled = useIsInternationalEventsEnabled();

  const { data, isLoading, isError } = useInternationalEventDetailQuery(eventKey);

  useEffect(() => {
    eventTracking.page.viewed({ pageName: 'international_event_page', url: window.location.href });
  }, [eventTracking]);

  useEffect(() => {
    if (data) {
      eventTracking.internationalEvent.viewed({
        eventId: data.id,
        eventName: data.title,
        eventDate: data.date ?? null,
        venue: data.city ?? '',
      });
    }
  }, [data, eventTracking]);

  // Feature turned off for this platform → don't expose the page.
  if (!isEnabled) {
    return <Navigate to={ROUTES.home} replace />;
  }

  if (isLoading) {
    return <LoaderOverlay />;
  }

  if (isError || !data) {
    return (
      <Flex dir='rtl' direction='column' align='center' justify='center' gap={4} py={20} px={6}>
        <Text textStyle='headlineMedium' color='dark.D900'>
          <FormattedMessage
            defaultMessage='لم يتم العثور على هذه الفعالية'
            id='internationalEvents.notFound'
          />
        </Text>
        <Button
          bg='green.G500'
          color='white'
          _hover={{ bg: 'green.G700' }}
          onClick={() => navigate(ROUTES.home)}
        >
          <FormattedMessage defaultMessage='العودة للرئيسية' id='internationalEvents.backHome' />
        </Button>
      </Flex>
    );
  }

  const { title, subtitle, imageLink, description, city, country, state, goodToKnow } = data;
  const isSoldOut = state === MatchState.SOLD_OUT;
  const location = [city, country].filter(Boolean).join(' - ');

  // Booking happens off-site (PlatinumList), so there is no local cart/checkout
  // funnel — track the CTA press as purchase intent before opening the tab.
  const handleBookNow = () => {
    eventTracking.internationalEvent.bookNowPressed({
      eventId: data.id,
      eventName: data.title,
      startingPrice: data.startingPrice,
      currency: data.currency,
    });
    window.open(data.iframeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box dir='rtl'>
      <EventHeader
        variant='international'
        title={title}
        subtitle={subtitle ?? undefined}
        imageLink={imageLink ?? ''}
        date={data.date}
        time={data.time ?? undefined}
        city={location || undefined}
        arDescription={description}
        eventKey={String(data.id)}
        eventName={title}
      />

      {/* Display-only booking CTA — replaces the concert cart/hall. Full-width to
          match the hero card. Opens PlatinumList checkout in a new tab so its
          queue/waiting-room cookies work first-party (an iframe would break them
          with ERR_TOO_MANY_REDIRECTS). */}
      <Box maxW='1440px' mx='auto' px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Button
          width='100%'
          size='lg'
          h={{ base: '52px', md: '56px' }}
          borderRadius='full'
          bg='green.G500'
          color='white'
          fontWeight='700'
          _hover={{ bg: 'green.G700' }}
          _disabled={{ bg: 'dark.D200', cursor: 'not-allowed', opacity: 1 }}
          isDisabled={isSoldOut}
          onClick={handleBookNow}
        >
          {isSoldOut ? (
            <FormattedMessage defaultMessage='Sold Out' id='concerts.soldOut' />
          ) : (
            <FormattedMessage defaultMessage='احجز الآن' id='internationalEvents.bookNow' />
          )}
        </Button>
      </Box>

      {/* Notes / notice cards — mirrors the concert page's good-to-know */}
      {goodToKnow && goodToKnow.length > 0 && <GoodToKnowCard items={goodToKnow} />}
    </Box>
  );
};
